import { IconButton, Stack, Tab, Tabs, Tooltip, styled } from "@mui/material";
import {
  Accessibility,
  ArrowBack,
  ArrowForward,
  LocalHospital,
  NoteAlt,
} from "@mui/icons-material";
import { ReactElement } from "react";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";

/**
 * Etapas do atendimento.
 * Cada etapa vira uma aba (Tab) com o seu ícone correspondente,
 * substituindo o antigo Stepper por uma navegação em abas mais moderna.
 */
const steps: { label: string; icon: ReactElement }[] = [
  { label: "Atendimento", icon: <Accessibility /> },
  { label: "Intervenções", icon: <LocalHospital /> },
  { label: "Documentos e assinatura", icon: <NoteAlt /> },
];

/**
 * Container das abas com visual moderno.
 * O indicador ocupa toda a altura da aba, formando um efeito de "pílula"
 * arredondada na aba selecionada (verde da identidade visual).
 */
const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 46,
  "& .MuiTabs-indicator": {
    height: "100%",
    borderRadius: 12,
    // Mesma cor primária do botão "Iniciar Teleatendimento" (primary800).
    backgroundColor: theme.palette.primary800.main,
    zIndex: 0,
  },
}));

/**
 * Aba individual. Fica acima do indicador (zIndex) e troca a cor do texto
 * para branco quando selecionada, mantendo bom contraste (acessibilidade).
 */
const StyledTab = styled(Tab)(({ theme }) => ({
  zIndex: 1,
  minHeight: 46,
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  transition: "color .2s ease, background-color .2s ease",
  // Hover sutil nas abas não selecionadas (fica atrás do indicador da ativa).
  "&:hover": {
    backgroundColor: "rgba(0, 124, 109, 0.08)",
    color: theme.palette.primary800.main,
  },
  "&.Mui-selected": {
    color: "#fff",
  },
  // Mantém o texto branco no hover da aba já selecionada (sobre o indicador verde).
  "&.Mui-selected:hover": {
    color: "#fff",
    backgroundColor: "transparent",
  },
}));

/**
 * Navegação das telas do atendimento em formato de abas.
 * Continua controlada pelo mesmo estado global (activeStep / setActiveStep)
 * do MedicalCareContext, sem criar novo estado.
 */
export function StepArea() {
  const { activeStep, setActiveStep } = useMedicalCareContext();

  // Estilo das setas de navegação: pequenas e arredondadas (circulares),
  // com borda sutil e visual desabilitado (cinza) na primeira/última tela.
  const arrowButtonSx = {
    flexShrink: 0,
    width: 32,
    height: 32,
    border: "1px solid",
    borderColor: "primary800.main",
    color: "primary800.main",
    "&:hover": { backgroundColor: "rgba(0, 124, 109, 0.08)" },
    "&.Mui-disabled": {
      borderColor: "neutral400.main",
      color: "neutral400.main",
    },
  };

  return (
    // Setas de navegação fixas flanqueando as abas: voltar à esquerda e
    // avançar à direita. Voltar fica indisponível na primeira tela e avançar
    // na última (terceira).
    <Stack direction="row" alignItems="center" spacing={1} width="100%">
      <Tooltip title="Voltar">
        {/* span necessário para o Tooltip funcionar com o botão desabilitado */}
        <span>
          <IconButton
            size="small"
            aria-label="Voltar para a etapa anterior"
            disabled={activeStep === 0}
            onClick={() => setActiveStep((prev: number) => prev - 1)}
            sx={arrowButtonSx}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <StyledTabs
        value={activeStep}
        onChange={(_, value) => setActiveStep(value)}
        // fullWidth aproveita todo o espaço horizontal disponível,
        // evitando espaços em branco excessivos entre as abas.
        variant="fullWidth"
        aria-label="Navegação das etapas do atendimento"
        // flex: 1 para as abas ocuparem o espaço entre as setas.
        sx={{ flex: 1, minWidth: 0 }}
      >
        {steps.map((step) => (
          <StyledTab
            key={step.label}
            icon={step.icon}
            iconPosition="start"
            label={step.label}
          />
        ))}
      </StyledTabs>

      <Tooltip title="Avançar">
        <span>
          <IconButton
            size="small"
            aria-label="Avançar para a próxima etapa"
            disabled={activeStep === 2}
            onClick={() => setActiveStep((prev: number) => prev + 1)}
            sx={arrowButtonSx}
          >
            <ArrowForward fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
